import {
	type FC,
	type RefObject,
	useEffect,
	useImperativeHandle,
	useRef,
} from 'react';
import { debounce, throttle } from '../../../../_helpers/performanceUtils.ts';

interface Props {
	lines: number;
	children: string;
	isInlineBlock?: boolean; // in some cases the element's height does not equal lineHeight * rows if the element is displayed "inline"
	wrapWordsInSpans?: boolean; // if true, it will wrap each word in a span
	wrapperRef?: RefObject<HTMLSpanElement | null>;
	onFinalTextDisplay?: () => void;
	useThrottle?: boolean;
	rateLimitTimout?: number;
}

/**
 * A -webkit-line-clamp JS implementation, but it abbreviates text based on words not characters.
 * @param text
 * @param lines
 * @param element
 * @param wrapWordsInSpans
 * @param onFinalTextDisplay
 */
const abbreviateTextUntilItFitsLinesInElement = (
	text: string,
	lines: number,
	element: HTMLSpanElement | null,
	wrapWordsInSpans: boolean,
	onFinalTextDisplay?: () => void
) => {
	if (!element) return;

	element.innerHTML = wrapWordsInSpans
		? `<span>${text.replaceAll(' ', ' </span><span>')}</span>`
		: text;

	const words = text.split(' ');

	const { lineHeight } = getComputedStyle(element);

	if (!lineHeight.includes('px')) return; // TODO: future improvement - make it compatible not only with px value

	const lineHeightPx: number = Number(lineHeight.split('px')[0]);

	const maxHeight: number = lineHeightPx * lines;

	const isElemTallerThanMax = () =>
		element.getBoundingClientRect().height > maxHeight;

	let popsLeft = 500;

	while (isElemTallerThanMax() && words.length > 0 && popsLeft > 0) {
		words.pop();

		element.innerHTML = wrapWordsInSpans
			? '<span>' + words.join(' </span><span>') + '...</span>'
			: words.join(' ') + '...';

		popsLeft--;
	}

	onFinalTextDisplay?.();
};

const LineClamp: FC<Props> = ({
	lines,
	children: text,
	isInlineBlock = true,
	wrapWordsInSpans = false,
	wrapperRef,
	onFinalTextDisplay,
	useThrottle = false,
	rateLimitTimout = 300,
}) => {
	const ref = useRef<HTMLSpanElement | null>(null);

	useImperativeHandle(wrapperRef, () => ref.current, [ref]);

	useEffect(() => {
		const abbreviateText = () =>
			abbreviateTextUntilItFitsLinesInElement(
				text,
				lines,
				ref.current,
				wrapWordsInSpans,
				onFinalTextDisplay
			);

		abbreviateText();
		document?.fonts?.ready?.then(abbreviateText);

		const limitRate = useThrottle ? throttle : debounce;

		const rateLimitedAbbreviateText = limitRate(
			abbreviateText,
			rateLimitTimout
		);

		window.addEventListener('resize', rateLimitedAbbreviateText);

		return () => {
			window.removeEventListener('resize', rateLimitedAbbreviateText);
		};
	}, [text, lines]);

	return (
		<span
			ref={ref}
			aria-label={text}
			style={isInlineBlock ? { display: 'inline-block' } : undefined}
		>
			{text}
		</span>
	);
};

LineClamp.displayName = 'LineClamp';

export default LineClamp;
